import { alpha, Tab, TableCell, Tabs, useMediaQuery, useTheme } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Snackbar from "@mui/material/Snackbar";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import React, { PropsWithChildren } from "react";
import { savePostponement } from "../api/savePostponement";
import { useAuth } from "../context/AuthContext";
import { Match, Postponement } from "../interfaces";
import DownloadSchedulePDFButton from "./DownloadSchedulePDFButton";
import { useMatchDetailsContext } from "./matchDetails/MatchDetailsContext";
import PostponementDialog from "./PostponementDialog";
import { displayTeamName } from "./TeamCard";
import { useOptionalTeamScheduleContext } from "./TeamScheduleContext";

interface ScheduleTableProps {
  matches: Match[];
  isMobile: boolean;
  showScores?: boolean;
  showViewToggles?: boolean;
  showDownloadButton?: boolean;
  // view: "all" | "home" | "away";
  // setView: (v: "all" | "home" | "away") => void;
  adminView?: boolean;
}

const ScheduleTable: React.FC<ScheduleTableProps> = ({
  matches,
  // isMobile,
  showScores = false,
  showViewToggles = false,
  showDownloadButton = false,
  // view: propView,
  // setView: propSetView,
  adminView = false,
}) => {
  // const location = useLocation();
  const isMobile = useMediaQuery("(max-width:600px)");
  const { isAuthenticated } = useAuth();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const context = useOptionalTeamScheduleContext();
  const view = context ? context.view : undefined;
  const setView = context ? context.setView : undefined;
  const { setMatchId } = useMatchDetailsContext();
  // const view = propView ?? context?.view;
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [, setShowGameDayById] = React.useState<number>(-1);
  const [snackbarMessage, setSnackbarMessage] = React.useState("");
  const [snackbarSeverity, setSnackbarSeverity] = React.useState<
    "success" | "error"
  >("success");
  const [postponementDialogOpen, setPostponementDialogOpen] =
    React.useState(false);
  const [selectedMatch, setSelectedMatch] = React.useState<any>(null);

  // Use filteredMatches from context if available and toggles are shown
  const tableRows =
    showViewToggles && context?.filteredMatches
      ? context.filteredMatches
      : matches;

  // const handleOpenMatchdayForm = (matchId?: number) => {
  //   if (matchId) {
  //     setMatchId(matchId);
  //     // Navigate with both state and query parameter for reliability
  //     navigate(`/gameday?matchId=${matchId}`, {
  //       state: { matchId: matchId },
  //       replace: false,
  //     });
  //   } else {
  //     navigate("/gameday");
  //   }
  // };

  // --- Set match list for navigation (admin only) ---
  React.useEffect(() => {
    // Only set for admin view
    if (typeof window !== "undefined" && Array.isArray(matches)) {
      (window as any).currentMatchList = matches.map((m) => m.id);
    }
  }, [matches]);

  // Helper to format date for mobile (truncate year)
  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // months are 0-based
    const year = String(date.getFullYear()).slice(-2); // last two digits
    return isMobile ? `${day}.${month}` : `${day}.${month}.${year}`;
  };

  const handleOpenPostponementForm = (matchId?: number) => {
    if (matchId && isAuthenticated) {
      setSelectedMatch(matches.find((match) => match.id === matchId) || null);
      setPostponementDialogOpen(true);
    }
  };

  const handleSavePostponement = async (data: Postponement) => {
    if (!selectedMatch) return;
    try {
      // You may need to get seasonId from props/context
      // const seasonId = selectedMatch.season_id || 1; // Replace with actual season id
      await savePostponement(selectedMatch.id, data);
      setSnackbarMessage("Spielverlegung gespeichert.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setPostponementDialogOpen(false);
    } catch (e) {
      setSnackbarMessage("Fehler beim Speichern der Spielverlegung.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };
  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    if (!setView) return;
    if (newValue === "all" || newValue === "home" || newValue === "away") {
      setView(newValue);
    }
  };

  return (
    <>
      {showViewToggles && setView && (
        <Tabs
          value={view}
          onChange={handleChange}
          variant={isSmall ? "fullWidth" : "standard"}
          sx={{
            mb: 2,
            p: 0.5,
            borderRadius: 2,
            backgroundColor: alpha("#ffffff", 0.84),
            border: `1px solid ${alpha(theme.palette.text.primary, 0.06)}`,
            boxShadow: `0 10px 22px ${alpha("#c9d8e7", 0.16)}`,
            "& .MuiTabs-indicator": {
              backgroundColor: "secondary.main",
              height: 3,
            },
          }}
        >
          <Tab
            label="Alle Spiele"
            value="all"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: { xs: ".8rem", sm: "1rem" },
              borderRadius: 1.5,
              color: "text.secondary",
              "&.Mui-selected": {
                color: "text.primary",
                fontWeight: 700,
              },
            }}
          />
          <Tab
            label="Heimspiele"
            value="home"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: { xs: ".8rem", sm: "1rem" },
              borderRadius: 1.5,
              color: "text.secondary",
              "&.Mui-selected": {
                color: "text.primary",
                fontWeight: 700,
              },
            }}
          />
          <Tab
            label="Auswärtsspiele"
            value="away"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: { xs: ".8rem", sm: "1rem" },
              borderRadius: 1.5,
              color: "text.secondary",
              "&.Mui-selected": {
                color: "text.primary",
                fontWeight: 700,
              },
            }}
          />
        </Tabs>
      )}
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: "0 14px 28px rgba(15, 23, 42, 0.08)",
          mb: 2,
          maxWidth: "100%",
          overflowX: "auto",
          mt: 1,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.text.primary, 0.06)}`,
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <ScheduleTableCell bold>Datum</ScheduleTableCell>
              <ScheduleTableCell bold>Heim</ScheduleTableCell>
              <ScheduleTableCell bold>Gast</ScheduleTableCell>
              {showScores && (
                <ScheduleTableCell bold>Ergebnis</ScheduleTableCell>
              )}
              {!isMobile && (
                <ScheduleTableCell bold>Spielort</ScheduleTableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(tableRows) && tableRows.length > 0
              ? tableRows.map((row: any, idx: number) => (
                  <TableRow
                    key={idx}
                    // hover={!!adminView || !adminView}
                    sx={{
                      background:
                        row.id === selectedMatch?.id
                          ? alpha(theme.palette.primary.main, 0.88)
                          : idx % 2 === 0
                          ? "#fff"
                          : "#f9f9f9", // Alternating row colors
                      cursor: "pointer",
                      fontSize: { xs: ".75rem", sm: "0.9rem" }, // smaller on mobile
                      color: row.id === selectedMatch?.id ? "#fff" : "inherit",
                    }}
                    onClick={() => {
                      setShowGameDayById(row.id);
                      setMatchId(row.id);
                      setSelectedMatch(row as Match); // Enable row selection highlighting
                      // handleOpenMatchdayForm(row.id);
                      // if (!adminView) setSelectedMatch(row as Match);
                      // else handleOpenMatchdayForm(row.id);
                    }}
                  >
                    <ScheduleTableCell
                      onClick={() => handleOpenPostponementForm(row.id)}
                    >
                      <Box
                        sx={{
                          color:
                            row.postponement && row.postponement.new_date
                              ? "#d32f2f"
                              : row.id === selectedMatch?.id
                              ? "#fff"
                              : theme.palette.text.primary,
                          cursor: adminView ? "pointer" : "default",
                          textDecoration: adminView ? "underline" : "none",
                          fontSize: { xs: ".7rem", sm: ".8rem" },
                          ml: ".2rem",
                          // fontWeight: 600,
                        }}
                      >
                        {row.postponement && row.postponement.new_date
                          ? formatDate(row.postponement.new_date)
                          : formatDate(row.date)}
                      </Box>
                    </ScheduleTableCell>

                    <ScheduleTableCell>{row.team_home.name}</ScheduleTableCell>
                    <ScheduleTableCell>
                      {displayTeamName(row.team_away)}
                    </ScheduleTableCell>

                    {showScores && (
                      <ScheduleTableCell>
                        {`${row.home_score} : ${row.away_score}`}
                      </ScheduleTableCell>
                    )}

                    {!isMobile && (
                      <ScheduleTableCell>
                        {row.spielort_link ? (
                          <a
                            href={row.spielort_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color:
                                row.id === selectedMatch?.id
                                  ? "#fff"
                                  : theme.palette.text.primary,
                              cursor: "pointer",
                              textDecoration: "underline",
                            }}
                          >
                            {typeof row.spielort === "object" &&
                            row.spielort !== null
                              ? row.spielort.name
                              : row.spielort || "-"}
                          </a>
                        ) : typeof row.spielort === "object" &&
                          row.spielort !== null ? (
                          row.spielort.name
                        ) : (
                          row.spielort || "-"
                        )}
                      </ScheduleTableCell>
                    )}
                  </TableRow>
                ))
              : null}
          </TableBody>
        </Table>
      </TableContainer>

      {showDownloadButton && <DownloadSchedulePDFButton />}
      {/* Modal for match details (public view only) */}
      {/* {!adminView && (
        <Dialog
          open={!!selectedMatch}
          onClose={() => setSelectedMatch(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 2,
              background: "#f2e9e4",
            }}
          >
            Spiel-Details
            <IconButton onClick={() => setSelectedMatch(null)} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3, background: "#fafbfc" }}>
            {selectedMatch && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box
                  sx={{
                    fontWeight: 700,
                    fontSize: 18,
                    color: "#1976d2",
                    textAlign: "center",
                  }}
                >
                  {selectedMatch.team_home?.name} vs.{" "}
                  {displayTeamName(selectedMatch.team_away)}
                </Box>
                <Box sx={{ textAlign: "center", fontSize: 16 }}>
                  <b>Datum:</b> {formatDate(selectedMatch.date)}
                </Box>
                {showScores && (
                  <Box sx={{ textAlign: "center", fontSize: 16 }}>
                    <b>Ergebnis:</b> {selectedMatch.home_score} :{" "}
                    {selectedMatch.away_score}
                  </Box>
                )}
                <Box sx={{ textAlign: "center", fontSize: 16 }}>
                  <b>Spielort:</b>{" "}
                  {selectedMatch.spielort?.name ||
                    (typeof selectedMatch.spielort === "string"
                      ? selectedMatch.spielort
                      : "-")}
                </Box>
                {selectedMatch.spielort &&
                  typeof selectedMatch.spielort === "object" &&
                  (selectedMatch.spielort.street ||
                    selectedMatch.spielort.plzOrt) && (
                    <Box sx={{ textAlign: "center", mt: 1 }}>
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                          `${selectedMatch.spielort.name}, ${selectedMatch.spielort.street}, ${selectedMatch.spielort.plzOrt}`
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#1976d2",
                          fontWeight: 600,
                          textDecoration: "underline",
                          fontSize: 16,
                        }}
                      >
                        Anfahrt mit Google Maps
                      </a>
                    </Box>
                  )}
              </Box>
            )}
          </DialogContent>
        </Dialog>
      )} */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
      {selectedMatch?.team_home && selectedMatch?.team_away && (
        <PostponementDialog
          open={postponementDialogOpen}
          onClose={() => setPostponementDialogOpen(false)}
          onSave={handleSavePostponement}
          match={selectedMatch}
        />
      )}
    </>
  );
};

export default ScheduleTable;

interface ScheduleTableCellProps extends PropsWithChildren {
  onClick?: () => void;
  bold?: boolean;
  wordBreak?: boolean;
}

const ScheduleTableCell = ({
  children,
  onClick,
  bold,
  wordBreak,
}: ScheduleTableCellProps) => {
  return (
    <TableCell
      onClick={onClick}
      sx={{
        fontWeight: bold ? 700 : 500,
        fontSize: { xs: ".65rem", sm: ".8rem" },
        wordBreak: wordBreak ? "break-word" : "normal",
        // textAlign: "center",
        paddingBlock: 0.4,
        color: "inherit",
      }}
    >
      {children}
    </TableCell>
  );
};
